import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from '../../constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from '../../slices/commonSelectors';
import type { ActionRollup } from '../IAP/hooks/actionRollupHooks';
import type { ActionDependencyBlocker } from '../IAP/utils/actionDependencies';

export type MyTaskScope = 'org' | 'incident';

/** Why the task is mine, so work held by a whole seat reads differently. */
export type MyTaskAssignmentReason = 'poc' | 'user' | 'position' | 'team';

export type MyTask = {
  scope: MyTaskScope;
  action_id: string;
  name: string;
  description: string | null;
  status: string;
  priority: string;
  start_date: string | null;
  target_date: string | null;
  assignee_kind: string | null;
  assignee_id: string | null;
  poc: string | null;
  assigned_via: MyTaskAssignmentReason;
  blocked_by: ActionDependencyBlocker[];
  incident_id: string | null;
  incident_name: string | null;
  group_guid: string | null;
};

export type MyTasksResponse = {
  rollup: ActionRollup;
  tasks: MyTask[];
};

/**
 * Deliberately outside the org and incident action key trees rather than under
 * one of them: this view spans both, so it has to invalidate on a mutation to
 * either. `useMyTasks` is refetched by the `['actions']` prefix that both
 * mutation paths already invalidate.
 */
export const myTaskKeys = {
  base: ['actions', 'mine'] as const,
  byUserAndGroup: (userGuid: string, groupGuid: string) =>
    [...myTaskKeys.base, userGuid, groupGuid] as const,
} as const;

export const useMyTasks = () => {
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);

  return useQuery({
    queryKey: myTaskKeys.byUserAndGroup(userGuid, groupGuid),
    queryFn: async () => {
      const resp = await axios.get<MyTasksResponse>(
        `${config.apiGateway.ics_actions}/mine`,
        { params: { group_guid: groupGuid } }
      );
      return resp.data;
    },
    enabled: !!userGuid,
  });
};

/**
 * Same `/actions/mine` cache as org My Tasks, narrowed to one incident so
 * assignment rules stay on the server and the two pages cannot disagree.
 */
export const useIncidentMyTasks = (incidentId: string | undefined) => {
  const query = useMyTasks();
  const tasks = useMemo(
    () =>
      incidentId
        ? (query.data?.tasks ?? []).filter(
            (task) => task.incident_id === incidentId
          )
        : [],
    [incidentId, query.data?.tasks]
  );

  return { ...query, tasks };
};
